import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, field, hint, label } from '../contacts/Sheet.tsx';
import { StatusTag } from '../StatusTag.tsx';
import { formatDate } from '../../dates.ts';
import {
  SEVERITIES, asCompletionEdits, isOpen, severityWord, useAssignees, useCloseTask, useCompleteTask, useEditCompletion,
  usePriority, useReassignTask, useOpenedTask, useStartTask, useTask, useTaskDepartments, useTaskVersions,
  type Severity, type Task, type TaskEvent,
} from '../../api/taskQueries.ts';
import { EarlierVersions, EditedMarker } from '../EarlierVersions.tsx';
import { useEditReasons } from '../../api/facilityPage.ts';
import { formatInstant, localValue, timeState, whenState } from '../../tasks/fields.ts';
import { Refusal, TimeField, WhenField, chip, go, plain, type DueValue } from './Fields.tsx';
import { DEATH_TASK_WORDS, isDeathTask, priorityWords, recordTarget, showsEstimate } from '../../tasks/model.ts';
import { FollowUpFields, NO_FOLLOW_UP, agencyToday, followUpToSend } from './FollowUp.tsx';
import { useMySections } from '../../api/navigation.ts';
import {
  OBJECT_WORDS, assignedBy, clientLink, closedByRecordSaid, closedRecordLink, departmentHeader, followUpSaid, objectLink,
  withObject,
} from '../../tasks/taskView.ts';
import { VisitDetails } from '../VisitDetails.tsx';
import { ClosedRecordFacts } from './ClosedRecordFacts.tsx';
import { ContactSheet } from '../contacts/ContactSheet.tsx';

/**
 * The task view (Arthur's layout, A-#152; 147, A-#77), the same everywhere a task opens: a header naming the
 * department ("Property Task"), the task, then four fields (the client, the object, who assigned it, when it is due),
 * then the completer's fields, open to fill in. The client's name opens the client page at the department's section;
 * the object opens its own page (taskView.ts). Who holds it, its priority, status, times and history are under More.
 * Every button only where the task's `may` says so, never re-derived here. OPENING THE CARD CHANGES NOTHING about the
 * task's status (A-#79): only Start moves it to In Progress, and only completing moves it to Done.
 */

type Mode = 'view' | 'reassign' | 'priority' | 'waive' | 'cancel';

export function TaskCard({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const q = useTask(taskId);
  // Opening is recorded once per card shown (route 7a): "started" for the holder, the status untouched. Never /start.
  const opened = useOpenedTask();
  const told = useRef<string | null>(null);
  useEffect(() => {
    if (told.current === taskId) return;
    told.current = taskId;
    opened.mutate(taskId);
  }, [taskId, opened]);
  const [mode, setMode] = useState<Mode>('view');
  const [said, setSaid] = useState<string | null>(null);
  const t = q.data?.task;
  return (
    <Sheet labelId={`task-${taskId}`} onClose={onClose}
           title={t ? <span className="flex flex-wrap items-center gap-2">{isOpen(t) && severityWord(t.severity) && <Mark task={t} />}{departmentHeader(t.department)}</span> : 'Task'}>
      {q.error && !q.data ? <Refusal error={q.error} />
        : !t ? <p className="text-neutral-700">Loading…</p>
        : mode === 'view' ? <View task={t} events={q.data!.events} onMode={setMode} said={said} onSaid={setSaid} />
        : mode === 'reassign' ? <ReassignForm task={t} onDone={() => setMode('view')} />
        : mode === 'priority' ? <PriorityForm task={t} onDone={() => setMode('view')} />
        : <CloseForm task={t} how={mode} onDone={() => setMode('view')} />}
    </Sheet>
  );
}

function Mark({ task }: { task: Task }) {
  const w = severityWord(task.severity);
  if (!w) return null;
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-bold tracking-wide uppercase ${
      task.severity === 'Critical' ? 'bg-critical-bg text-critical-fg' : 'border border-neutral-400 text-neutral-800'}`}>{w}</span>
  );
}

const EVENT_WORDS: Record<TaskEvent['kind'], string> = {
  created: 'Created', assigned: 'Assigned', reassigned: 'Reassigned', seen: 'Seen', started: 'Started',
  completed: 'Completed', waived: 'Waived', cancelled: 'Cancelled', priority: 'Priority changed',
  reopened: 'Reopened',
  edited: 'Completion edited',
};

function View({ task: t, events, onMode, said, onSaid }: {
  task: Task; events: TaskEvent[]; onMode: (m: Mode) => void; said: string | null; onSaid: (s: string | null) => void;
}) {
  const start = useStartTask();
  const sections = useMySections().data?.sections ?? [];
  const [contact, setContact] = useState<string | null>(null);
  const [visit, setVisit] = useState<string | null>(null);
  const open = isOpen(t);
  const record = recordTarget(t);
  const row = (k: string, v: React.ReactNode) => (
    <div className="flex flex-wrap gap-x-3 py-1"><dt className="w-36 shrink-0 text-sm font-semibold text-neutral-700">{k}</dt><dd className="min-w-0 flex-1 text-neutral-900">{v}</dd></div>
  );
  const o = t.object;
  const to = o && t.client ? objectLink(t.client.id, o, sections) : null;
  const objectWords = o ? (o.label ?? OBJECT_WORDS[o.kind]) : null;
  return (
    <div>
      <p className="text-lg font-semibold text-neutral-900">{t.title}</p>
      {isDeathTask(t) && <p className="mt-1 text-neutral-700">{DEATH_TASK_WORDS}.</p>}
      {t.detail && <p className="mt-1 whitespace-pre-wrap text-neutral-900">{t.detail}</p>}
      <dl className="mt-2 divide-y divide-neutral-200" aria-label="The task">
        {t.client && row('Client', (
          <span className="flex flex-wrap items-center gap-2">
            <Link className="link" to={clientLink(t.client.id, t.department, sections)}>{t.client.name}</Link>
            <span className="font-mono text-sm text-neutral-700">{t.client.ref}</span>
            <StatusTag status={t.client.status} />
          </span>
        ))}
        {o && row(OBJECT_WORDS[o.kind], to === 'sheet' ? (
          <button type="button" className="link min-h-[44px] text-left" onClick={() => setContact(o.id)}>{objectWords}</button>
        ) : to ? <Link className="link" to={to}>{objectWords}</Link> : objectWords)}
        {row('Assigned by', assignedBy(t, events))}
        {row('Due', t.due_date ? `${formatDate(t.due_date)}${showsEstimate(t) ? ' (estimate)' : ''}` : 'No due date')}
      </dl>
      {contact && <ContactSheet contactId={contact} linkId="" onClose={() => setContact(null)} />}

      {said && <p role="status" className="mt-3 font-semibold text-neutral-900">{said}</p>}
      {t.completion && <Completion task={t} completion={t.completion} />}
      {t.closed_by_record && t.client && <ClosedByRecordNote task={t} clientId={t.client.id} sections={sections} onVisit={setVisit} editing={visit !== null} />}
      {visit && <VisitDetails id={visit} onClose={() => setVisit(null)} />}
      {t.closed && !t.closed_by_record && (
        <p className="mt-3 text-neutral-900">
          {t.closed.kind === 'System' ? 'Closed by the system' : `${t.closed.kind}${t.closed.by ? ` by ${t.closed.by.name}` : ''}`}, {formatInstant(t.closed.at)}
          {t.closed.reason ? `: ${t.closed.reason}` : ''}
        </p>
      )}

      {/* The completer's fields, open to fill in, where the task's may allows it. A deadline with its own record
          (A-#83 A1) is completed by making that record, on its own screen: its button instead. */}
      {open && t.may.complete && !record && (
        <section aria-labelledby={`done-${t.id}`} className="mt-4 rounded-lg border border-neutral-300 px-3 py-3">
          <h3 id={`done-${t.id}`} className="text-sm font-bold tracking-wide text-neutral-700 uppercase">What happened</h3>
          <CompleteForm task={t} onSaid={onSaid} />
        </section>
      )}
      {open && (
        <div className="mt-4 flex flex-wrap gap-2">
          {t.may.start && t.status === 'Open' && (
            <button type="button" className={plain} disabled={start.isPending} onClick={() => start.mutate(t.id)}>Start</button>
          )}
          {record && <Link to={record.to} state={record.state} className={`${go} inline-flex items-center`}>{record.label}</Link>}
          {t.may.reassign && <button type="button" className={plain} onClick={() => onMode('reassign')}>Reassign</button>}
          {t.may.priority && <button type="button" className={plain} onClick={() => onMode('priority')}>Priority</button>}
          {t.may.waive && <button type="button" className={plain} onClick={() => onMode('waive')}>Waive</button>}
          {t.may.cancel && <button type="button" className={plain} onClick={() => onMode('cancel')}>Cancel task</button>}
        </div>
      )}
      <Refusal error={start.error} />

      <details className="mt-4">
        <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-semibold text-neutral-800">More</summary>
        <dl className="divide-y divide-neutral-200">
          {row('With', t.assigned_to ? t.assigned_to.name : 'Unassigned: the department\'s queue')}
          {row('Priority', priorityWords(t))}
          {row('Status', t.status)}
          {t.is_statutory && row('Statutory', 'Yes')}
        </dl>
        <h3 className="mt-3 text-sm font-bold tracking-wide text-neutral-700 uppercase">Times</h3>
        <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4" aria-label="Task times">
          {([['Assigned', t.times.assigned_at], ['Seen', t.times.seen_at], ['Started', t.times.started_at], ['Completed', t.times.completed_at]] as const).map(([k, v]) => (
            <div key={k}><dt className="text-sm font-semibold text-neutral-700">{k}</dt><dd className="text-neutral-900 tabular-nums">{formatInstant(v) ?? 'Not yet'}</dd></div>
          ))}
        </dl>
        <h3 className="mt-3 text-sm font-bold tracking-wide text-neutral-700 uppercase">History ({events.length})</h3>
        <ol className="divide-y divide-neutral-200">
          {events.map((e, i) => (
            <li key={i} className="py-1.5 text-sm text-neutral-900">
              <span className="font-semibold">{EVENT_WORDS[e.kind]}</span>{' '}
              <span className="tabular-nums text-neutral-700">{formatInstant(e.at)}</span>
              {e.holder && <span> · with {e.holder.name}</span>}
              {e.by && <span> · by {e.by.name}</span>}
              {e.detail && <span className="block text-neutral-700">{e.detail}</span>}
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function CompleteForm({ task, onSaid }: { task: Task; onSaid: (s: string | null) => void }) {
  const complete = useCompleteTask();
  const departments = useTaskDepartments();
  // A follow-up only in a department the server's follow_up list holds (contract v3.1).
  const mayFollowUp = (departments.data?.follow_up ?? []).includes(task.department);
  const today = agencyToday();
  const [note, setNote] = useState('');
  const [time, setTime] = useState('');
  const [when, setWhen] = useState(() => localValue(new Date()));
  const [follow, setFollow] = useState(NO_FOLLOW_UP as { on: boolean; title: string; due: DueValue; to: string });
  const spent = timeState(time);
  const at = whenState(when, new Date());
  const drafted = mayFollowUp ? followUpToSend(follow, today) : undefined;
  // 151: the follow-up is about the same object as the task, when the reader sees one.
  const fu = drafted ? withObject(drafted, task.object) : drafted;
  const ready = note.trim() !== '' && 'minutes' in spent && 'at' in at && fu !== null;
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      complete.mutate({ id: task.id, note: note.trim(), spent: { minutes: (spent as { minutes: number }).minutes }, happened_at: (at as { at: string }).at, ...(fu ? { follow_up: fu } : {}) },
        { onSuccess: (r) => onSaid(followUpSaid(r.follow_up)) });
    }} className="mt-2 space-y-3">
      <label className={label}>Note
        <textarea required value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={4000} className={`${field} py-2`} />
      </label>
      <TimeField value={time} onChange={setTime} />
      <WhenField value={when} onChange={setWhen} />
      {mayFollowUp && <FollowUpFields department={task.department} taskId={task.id} about={task.object} today={today} value={follow} onChange={setFollow} />}
      {!ready && <p className={hint}>A note, the time spent and when it happened are needed{follow.on ? ', and the follow-up\'s title and due date' : ''}.</p>}
      <Refusal error={complete.error} />
      <button type="submit" className={go} disabled={!ready || complete.isPending}>Complete the task</button>
    </form>
  );
}

/**
 * The completion as it stands now (153, Arthur, Ruling 2), shown once; when it was edited, the "Edited" marker and
 * "See earlier versions", the same component as visits and communications. "Edit" where may.edit_completion says so:
 * its completer and tiers 1-2, never a task closed by its own record (the server decides).
 */
function Completion({ task, completion: c }: { task: Task; completion: NonNullable<Task['completion']> }) {
  const [editing, setEditing] = useState(false);
  const versions = useTaskVersions(task.id, c.edited);
  const edits = versions.data ? asCompletionEdits(versions.data.versions) : undefined;
  return (
    <div className="mt-3 rounded-lg border border-neutral-300 px-3 py-2">
      <p className="text-sm font-semibold text-neutral-700">Done{c.by ? ` by ${c.by.name}` : ''}, {c.minutes} min</p>
      <p className="text-sm text-neutral-800">Happened {formatInstant(c.happened_at)} · recorded {formatInstant(c.recorded_at)}</p>
      <p className="mt-1 whitespace-pre-wrap text-neutral-900">{c.note}</p>
      {c.edited && <EditedMarker edits={edits} />}
      {c.edited && <EarlierVersions edits={edits} loading={versions.isLoading} error={versions.error as Error | null} />}
      {task.may.edit_completion && !editing && (
        <button type="button" className={plain} onClick={() => setEditing(true)}>Edit</button>
      )}
      {editing && <EditCompletionForm task={task} completion={c} onDone={() => setEditing(false)} />}
    </div>
  );
}

/** Correct the completion: only what changed is sent, with a reason from the shared four (Other with words). */
function EditCompletionForm({ task, completion: c, onDone }: {
  task: Task; completion: NonNullable<Task['completion']>; onDone: () => void;
}) {
  const edit = useEditCompletion();
  const reasons = useEditReasons(true);
  const action = task.source === 'Action';
  const [what, setWhat] = useState(task.title);
  const [note, setNote] = useState(c.note);
  const [time, setTime] = useState(String(c.minutes));
  const [when, setWhen] = useState(() => localValue(new Date(c.happened_at)));
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const spent = timeState(time);
  const at = whenState(when, new Date());
  const needsText = reasons.data?.reasons.find((r) => r.code === reason)?.needs_text ?? false;
  const changes = {
    ...(action && what.trim() !== task.title ? { what: what.trim() } : {}),
    ...(note.trim() !== c.note ? { note: note.trim() } : {}),
    ...('minutes' in spent && spent.minutes !== c.minutes ? { minutes: spent.minutes } : {}),
    ...('at' in at && when !== localValue(new Date(c.happened_at)) ? { happened_at: at.at } : {}),
  };
  const ready = Object.keys(changes).length > 0 && (!action || what.trim() !== '') && note.trim() !== ''
    && 'minutes' in spent && 'at' in at && reason !== '' && (!needsText || reasonText.trim() !== '');
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      edit.mutate({ id: task.id, reason, reason_text: reasonText.trim() || null, ...changes }, { onSuccess: onDone });
    }} className="mt-3 space-y-3 border-t border-neutral-300 pt-3">
      {action && (
        <label className={label}>What was done
          <input value={what} onChange={(e) => setWhat(e.target.value)} maxLength={200} className={field} />
        </label>
      )}
      <label className={label}>Note
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={4000} className={`${field} py-2`} />
      </label>
      <TimeField value={time} onChange={setTime} />
      <WhenField value={when} onChange={setWhen} />
      <label className={label}>Why it is being edited
        <select value={reason} onChange={(e) => setReason(e.target.value)} className={field}>
          <option value="">Choose a reason</option>
          {(reasons.data?.reasons ?? []).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
      </label>
      {needsText && (
        <label className={label}>Say why
          <input value={reasonText} onChange={(e) => setReasonText(e.target.value)} maxLength={2000} className={field} />
        </label>
      )}
      {!ready && <p className={hint}>Change something, and say why. The earlier version is kept.</p>}
      <Refusal error={edit.error} />
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={go} disabled={!ready || edit.isPending}>Save the correction</button>
        <button type="button" className={plain} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function ReassignForm({ task, onDone }: { task: Task; onDone: () => void }) {
  const people = useAssignees(task.department, 'assign');
  const reassign = useReassignTask();
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      if (to === '') return;
      reassign.mutate({ id: task.id, assigned_to_user_id: to === 'queue' ? null : to, ...(reason.trim() ? { reason: reason.trim() } : {}) }, { onSuccess: onDone });
    }} className="space-y-3">
      <label className={label}>Give it to ({task.department})
        <select value={to} onChange={(e) => setTo(e.target.value)} className={field}>
          <option value="">Choose…</option>
          {people.data?.queue_allowed && <option value="queue">Nobody: the department's queue</option>}
          {people.data?.people.filter((p) => p.id !== task.assigned_to?.id).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
        </select>
      </label>
      <label className={label}>Why (optional)
        <input type="text" maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} className={field} />
      </label>
      <Refusal error={reassign.error ?? people.error} />
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={go} disabled={to === '' || reassign.isPending}>Reassign</button>
        <button type="button" className={plain} onClick={onDone}>Back</button>
      </div>
    </form>
  );
}

function PriorityForm({ task, onDone }: { task: Task; onDone: () => void }) {
  const priority = usePriority();
  // 155: starts from what is stored. On a risen task every choice is open (keeping Normal is a person's choice that
  // stands, ruling c); otherwise only a change.
  const [sev, setSev] = useState<Severity>(task.severity_stored);
  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="Priority" className="flex flex-wrap gap-2">
        {SEVERITIES.map((s) => (
          <button key={s} type="button" role="radio" aria-checked={sev === s} className={chip(sev === s)} onClick={() => setSev(s)}>
            {severityWord(s) ? `${severityWord(s)} (${s})` : s}
          </button>
        ))}
      </div>
      <Refusal error={priority.error} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={go} disabled={(!task.priority_risen && sev === task.severity_stored) || priority.isPending}
                onClick={() => priority.mutate({ id: task.id, severity: sev }, { onSuccess: onDone })}>Change priority</button>
        <button type="button" className={plain} onClick={onDone}>Back</button>
      </div>
    </div>
  );
}

function CloseForm({ task, how, onDone }: { task: Task; how: 'waive' | 'cancel'; onDone: () => void }) {
  const close = useCloseTask();
  const [reason, setReason] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (reason.trim()) close.mutate({ id: task.id, how, reason: reason.trim() }, { onSuccess: onDone }); }} className="space-y-3">
      <p className="text-neutral-900">
        {how === 'waive' ? 'Waiving says the work is not needed.' : 'Cancelling takes the task away.'} It never comes back by itself.
      </p>
      <label className={label}>Why
        <textarea required rows={3} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} className={`${field} py-2`} />
      </label>
      <Refusal error={close.error} />
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={go} disabled={!reason.trim() || close.isPending}>{how === 'waive' ? 'Waive the task' : 'Cancel the task'}</button>
        <button type="button" className={plain} onClick={onDone}>Back</button>
      </div>
    </form>
  );
}

/**
 * A-#194: a Done task closed by its own record says which, its day and who recorded it, and opens THAT record (its
 * card at the visit, its filing's row, the visit's sheet), where it is corrected: 153 never edits such a completion.
 */
function ClosedByRecordNote({ task: t, clientId, sections, onVisit, editing }: {
  task: Task; clientId: string; sections: readonly import('../../api/navigation.ts').SectionKey[]; onVisit: (id: string) => void;
  /** a welfare visit opened full (its own details, with Edit and Withdraw): its read-only copy steps aside */
  editing: boolean;
}) {
  const r = t.closed_by_record!;
  const to = closedRecordLink(clientId, r, sections);
  const full = 'Open full screen \u2192';
  return (
    <div className="mt-3 rounded-lg border border-neutral-300 px-3 py-2" aria-label="What closed this task">
      <p className="font-semibold text-neutral-900">{closedByRecordSaid(r)}</p>
      {/* A-#205: the record itself, read only, as it shows itself; nothing the reader could not open */}
      {!editing && <ClosedRecordFacts clientId={clientId} r={r} sections={sections} />}
      {to === 'visit' ? (
        <button type="button" className="link mt-1 min-h-[44px]" onClick={() => onVisit(r.id)}>{full}</button>
      ) : to ? (
        <Link className="link mt-1 inline-flex min-h-[44px] items-center" to={to.to} state={to.state}>{full}</Link>
      ) : null}
    </div>
  );
}
